import { useMemo } from 'react';
import { _isBlank, _isNil, _notNil, safelyDecodeURIComponent } from '@/littledash';
import { isSubsetOfSets } from '@/utils/browser.utils';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import {
  GroupTarget,
  PlannedAnimalTarget,
  RegisteredAnimalTarget,
  SampleDefinitionFormData,
  SampleDefinitionIssue,
  SampleDefinitionMetadataV1,
  SampleDetailType,
} from '@/model/SamplePlan.model';
import { TeamGlossaryApiId, TeamGlossaryV1 } from '@/model/Glosary.model';
import { MetadataGlossaryApiId, MetadataGlossaryV1 } from '@/model/Metadata.model';
import { Nullable } from '@/model/Common.model';

/**
 * Custom hook that validates sample definitions and returns issues grouped by definition ID.
 * Replaces the partial validation logic from SamplingPlanStep.tsx.
 *
 * This hook is context-agnostic and works in both pre-study create (Redux state) and
 * post-study create (API response) contexts. Components pass data directly from their
 * respective sources, and the hook performs validation without knowing the data origin.
 *
 * @param sampleDefinitions - Array of sample definitions to validate
 * @param sampleGlossary - Array of sample types with their configurations
 * @param metadataGlossary - Array of metadata glossary entries
 * @param groups - Array of treatment groups in the study
 * @param plannedAnimals - Array of planned animals in the study
 * @param registeredAnimals - Array of registered animals in the study
 * @returns Map of definition IDs to their validation issues
 */
export const useSamplePlanValidation = (
  sampleDefinitions: Nullable<Array<SampleDefinitionFormData>>,
  sampleGlossary: Nullable<Array<TeamGlossaryV1>>,
  metadataGlossary: Nullable<Array<MetadataGlossaryV1>>,
  groups: Nullable<Array<GroupTarget>>,
  plannedAnimals: Nullable<Array<PlannedAnimalTarget>>,
  registeredAnimals: Nullable<Array<RegisteredAnimalTarget>>
): { count: number; issues?: Map<SampleDefinitionFormData['api_id'], Array<SampleDefinitionIssue>> } => {
  return useMemo(() => {
    if (
      _notNil(sampleDefinitions) &&
      _notNil(sampleGlossary) &&
      _notNil(metadataGlossary) &&
      _notNil(groups) &&
      _notNil(plannedAnimals) &&
      _notNil(registeredAnimals)
    ) {
      const sampleGlossaryMap = new Map(sampleGlossary.map((gly) => [gly.api_id, gly]));
      const metadataGlossaryMap = new Map(metadataGlossary.map((mgl) => [mgl.api_id as MetadataGlossaryApiId, mgl]));
      const groupApiIds = new Set(groups.map((g) => g.api_id));
      const plannedAnimalApiIds = new Set(plannedAnimals.map((pa) => pa.api_id));
      const registeredAnimalApiIds = new Set(registeredAnimals.map((ra) => ra.api_id));

      const issues = new Map<SampleDefinitionFormData['api_id'], Array<SampleDefinitionIssue>>();

      sampleDefinitions.forEach((definition) => {
        const definitionIssues: Array<SampleDefinitionIssue> = [];

        const archivedIssue = validateArchivedSampleType(definition, sampleGlossaryMap);
        if (_notNil(archivedIssue)) {
          definitionIssues.push(archivedIssue);
        }

        const missingSampleTypeIssue = validateMissingSampleType(definition, sampleGlossaryMap);
        if (_notNil(missingSampleTypeIssue)) {
          definitionIssues.push(missingSampleTypeIssue);
        }

        const invalidDetailsIssue = validateSampleDetails(definition, sampleGlossaryMap);
        if (_notNil(invalidDetailsIssue)) {
          definitionIssues.push(invalidDetailsIssue);
        }

        const metadataIssues = validateMetadata(definition, metadataGlossaryMap);
        definitionIssues.push(...metadataIssues);

        const requiredMetadataIssue = validateRequiredMetadata(definition, metadataGlossaryMap);
        if (_notNil(requiredMetadataIssue)) {
          definitionIssues.push(requiredMetadataIssue);
        }

        const targetIssue = validateTargets(
          definition,
          groupApiIds,
          plannedAnimalApiIds,
          registeredAnimalApiIds,
          groups,
          registeredAnimals
        );
        if (_notNil(targetIssue)) {
          definitionIssues.push(targetIssue);
        }

        if (definitionIssues.length > 0) {
          issues.set(definition.api_id, definitionIssues);
        }
      });

      return { count: Array.from(issues.values()).reduce((sum, issueList) => sum + issueList.length, 0), issues };
    }
    return { count: 0 };
  }, [sampleDefinitions, sampleGlossary, metadataGlossary, groups, plannedAnimals, registeredAnimals]);
};

const validateArchivedSampleType = (
  definition: SampleDefinitionFormData,
  sampleGlossaryMap: Map<TeamGlossaryApiId, TeamGlossaryV1>
): SampleDefinitionIssue | null => {
  const sampleType = sampleGlossaryMap.get(definition.sample_type);

  if (_notNil(sampleType) && _notNil(sampleType.archived_at)) {
    return {
      type: 'archived-sample-type',
      sample_type_id: definition.sample_type,
      sample_type_name: sampleType.title ?? 'Unknown',
    };
  }

  return null;
};

const validateMissingSampleType = (
  definition: SampleDefinitionFormData,
  sampleGlossaryMap: Map<TeamGlossaryApiId, TeamGlossaryV1>
): SampleDefinitionIssue | null => {
  if (!sampleGlossaryMap.has(definition.sample_type)) {
    return {
      type: 'missing-sample-type',
      sample_type_id: definition.sample_type,
    };
  }
  return null;
};

const validateSampleDetails = (
  definition: SampleDefinitionFormData,
  sampleGlossaryMap: Map<TeamGlossaryApiId, TeamGlossaryV1>
): SampleDefinitionIssue | null => {
  const sampleType = sampleGlossaryMap.get(definition.sample_type);

  if (_notNil(sampleType)) {
    const definitionDetailTypes = new Set(definition.details.map((detail) => detail.type));
    const allowedDetailTypes = new Set((sampleType.options?.details ?? []) as Array<SampleDetailType>);

    if (!isSubsetOfSets(definitionDetailTypes, allowedDetailTypes)) {
      const invalidTypes = Array.from(definitionDetailTypes).filter((type) => !allowedDetailTypes.has(type));
      return {
        type: 'invalid-sample-detail-type',
        invalid_detail_types: invalidTypes,
        sample_type_name: sampleType.title ?? 'Unknown',
      };
    }
  }

  return null;
};

const validateMetadata = (
  definition: SampleDefinitionFormData,
  metadataGlossaryMap: Map<MetadataGlossaryApiId, MetadataGlossaryV1>
): Array<SampleDefinitionIssue> => {
  const issues: Array<SampleDefinitionIssue> = [];
  const inactiveMetadataIds: Array<MetadataGlossaryApiId> = [];
  const missingMetadataIds: Array<MetadataGlossaryApiId> = [];
  const metadataNames: Array<string> = [];

  definition.metadata.forEach((md) => {
    const glossaryEntry = metadataGlossaryMap.get(md.type);

    if (_isNil(glossaryEntry)) {
      missingMetadataIds.push(md.type);
    } else if (!glossaryEntry.active) {
      inactiveMetadataIds.push(md.type);
      metadataNames.push(glossaryEntry.title ?? 'Unknown');
    } else {
      // Validate metadata options for select/multi-select fields
      const optionsIssue = validateMetadataOptions(md, glossaryEntry);
      if (_notNil(optionsIssue)) {
        issues.push(optionsIssue);
      }
    }
  });

  if (inactiveMetadataIds.length > 0) {
    issues.push({
      type: 'inactive-metadata',
      inactive_metadata_ids: inactiveMetadataIds,
      metadata_names: metadataNames,
    });
  }

  if (missingMetadataIds.length > 0) {
    issues.push({
      type: 'missing-metadata',
      missing_metadata_ids: missingMetadataIds,
    });
  }

  return issues;
};

const isMetadataValueEmpty = (value: unknown, fieldType: string): boolean => {
  if (_isBlank(value as string)) {
    return true;
  }

  // Special handling for multi_select fields
  if (fieldType === 'multi_select') {
    try {
      const parsed = JSON.parse(value as string);
      if (Array.isArray(parsed)) {
        // Empty array or array of only blank values
        return parsed.length === 0 || parsed.every((v) => _isBlank(v));
      }
    } catch {
      // If parse fails, fall back to blank check (already done above)
    }
  }

  return false;
};

const validateRequiredMetadata = (
  definition: SampleDefinitionFormData,
  metadataGlossaryMap: Map<MetadataGlossaryApiId, MetadataGlossaryV1>
): SampleDefinitionIssue | null => {
  const existingMetadata = new Map(definition.metadata.map((md) => [md.type, md]));

  const missingRequired: Array<{
    metadata_id: MetadataGlossaryApiId;
    metadata_name: string;
  }> = [];

  metadataGlossaryMap.forEach((glossaryEntry, metadataId) => {
    if (!glossaryEntry.active || !glossaryEntry.required) {
      return;
    }

    const existingMeta = existingMetadata.get(metadataId);

    if (_isNil(existingMeta) || isMetadataValueEmpty(existingMeta.value, glossaryEntry.field_type)) {
      missingRequired.push({
        metadata_id: metadataId,
        metadata_name: glossaryEntry.title ?? 'Unknown',
      });
    }
  });

  if (missingRequired.length > 0) {
    return {
      type: 'missing-required-metadata',
      missing_required_metadata: missingRequired,
    };
  }

  return null;
};

const validateMetadataOptions = (
  metadata: SampleDefinitionMetadataV1,
  glossaryEntry: MetadataGlossaryV1
): SampleDefinitionIssue | null => {
  const { field_type, options } = glossaryEntry;

  if ((field_type !== 'select' && field_type !== 'multi_select') || options.length === 0) {
    return null;
  }

  if (_isBlank(metadata.value as string)) {
    return null;
  }

  const value = metadata.value as string;
  const decodedOptionsSet = new Set(options.map((opt) => safelyDecodeURIComponent(opt)));

  if (field_type === 'select') {
    const decodedValue = safelyDecodeURIComponent(value);
    if (!decodedOptionsSet.has(decodedValue)) {
      return {
        type: 'invalid-metadata-value',
        metadata_id: metadata.type,
        metadata_name: glossaryEntry.title ?? 'Unknown',
        stored_value: value,
        available_options: options,
        field_type: 'select',
      };
    }
  } else if (field_type === 'multi_select') {
    let values: Array<string> = [];

    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        values = parsed;
      } else {
        ExceptionHandler.captureException(
          new InVivoError('Multi-select metadata contains non-array JSON', {
            slug: 'multi-select-invalid-json-type',
            context: {
              metadata_id: metadata.type,
              metadata_name: glossaryEntry.title,
              stored_value: value,
              parsed_type: typeof parsed,
            },
          })
        );
        values = [value];
      }
    } catch (parseError) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to parse multi-select metadata JSON', {
          slug: 'multi-select-json-parse-failure',
          cause: parseError as Error,
          context: {
            metadata_id: metadata.type,
            metadata_name: glossaryEntry.title,
            stored_value: value,
          },
        })
      );
      values = [value];
    }

    const invalidValues = values.filter((v) => !decodedOptionsSet.has(safelyDecodeURIComponent(v)));
    if (invalidValues.length > 0) {
      return {
        type: 'invalid-metadata-value',
        metadata_id: metadata.type,
        metadata_name: glossaryEntry.title ?? 'Unknown',
        stored_value: value,
        available_options: options,
        field_type: 'multi_select',
        invalid_values: invalidValues,
      };
    }
  }

  return null;
};

const mapIdsToNames = <T extends { api_id: string; name: string }>(
  entities: Array<T>,
  ids: Array<string>
): Array<string> => {
  const namesMap = new Map(entities.map((e) => [e.api_id, e.name]));
  return ids.reduce<Array<string>>((acc, id) => {
    const name = namesMap.get(id);
    if (_notNil(name)) {
      acc.push(name);
    }
    return acc;
  }, []);
};

const validateTargets = (
  definition: SampleDefinitionFormData,
  groupApiIds: Set<GroupTarget['api_id']>,
  plannedAnimalApiIds: Set<PlannedAnimalTarget['api_id']>,
  registeredAnimalApiIds: Set<RegisteredAnimalTarget['api_id']>,
  groups: Array<GroupTarget>,
  registeredAnimals: Array<RegisteredAnimalTarget>
): SampleDefinitionIssue | null => {
  switch (definition.target.type) {
    case 'groups': {
      const definitionGroupIds = new Set(definition.target.targets);
      if (!isSubsetOfSets(definitionGroupIds, groupApiIds)) {
        const missingIds = Array.from(definitionGroupIds).filter((id) => !groupApiIds.has(id));
        const groupNames = mapIdsToNames(groups, missingIds);

        return {
          type: 'invalid-target',
          target: {
            type: 'groups',
            missing_group_ids: missingIds,
            group_names: groupNames,
          },
        };
      }
      break;
    }
    case 'planned_animals': {
      const definitionAnimalIds = new Set(definition.target.targets);
      if (!isSubsetOfSets(definitionAnimalIds, plannedAnimalApiIds)) {
        const missingIds = Array.from(definitionAnimalIds).filter((id) => !plannedAnimalApiIds.has(id));
        return {
          type: 'invalid-target',
          target: {
            type: 'planned_animals',
            missing_animal_ids: missingIds,
          },
        };
      }
      break;
    }
    case 'registered_animals': {
      const definitionAnimalIds = new Set(definition.target.targets);
      if (!isSubsetOfSets(definitionAnimalIds, registeredAnimalApiIds)) {
        const missingIds = Array.from(definitionAnimalIds).filter((id) => !registeredAnimalApiIds.has(id));
        const animalNames = mapIdsToNames(registeredAnimals, missingIds);

        return {
          type: 'invalid-target',
          target: {
            type: 'registered_animals',
            missing_animal_ids: missingIds,
            animal_names: animalNames,
          },
        };
      }
      break;
    }
  }

  return null;
};
